import { env } from "environment-interface/env";
import { withBasePath } from "isomorphic/utils";
import { Avatar, Box, styled, Tooltip } from "prism-react";

import type { AppId } from "utils/clientAppId";
import { appId, fallbackAppId } from "utils/clientAppId";

import type { ClientAvatarProps } from "./types";

interface AppDetails {
  id: AppId;
  image?: string;
  name: string;
}

const FocusableAvatar = styled(Avatar, {
  transition: "opacity $fast",
  variants: {
    dimmed: {
      true: {
        transitionTimingFunction: "ease-in",
        opacity: 0.3,
      },
      false: {
        transitionTimingFunction: "ease-out",
      },
    },
  },
});

const appDetailsMap: Record<AppId, AppDetails> = {
  codesandbox: {
    id: "codesandbox",
    name: "CodeSandbox",
  },
  unknown: {
    id: "unknown",
    name: "",
  },
  sdk: {
    id: "sdk",
    name: "CodeSandbox SDK",
  },
  vscode: {
    id: "vscode",
    image: withBasePath(env, "/imgs/vscode.svg"),
    name: "VS Code",
  },
  playjs: {
    id: "playjs",
    image: withBasePath(env, "/imgs/mobile.svg"),
    name: "Mobile",
  },
  "inline-preview": {
    id: "inline-preview",
    name: "Inline Preview",
  },
};

const ClientSourceWrapper = styled("div", {
  position: "absolute",
  bottom: "-5px",
  right: "-1px",
  background: "$neutral-bg-base",
  width: "15px",
  height: "15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
});

const AppImage = styled("img", {
  width: "10px",
  height: "10px",
});

// TODO: possibly should be the default Tooltip behaviour?
const OptionalTooltip: React.FC<{
  content?: string;
  children: React.ReactNode;
}> = ({ children, content }) => {
  if (!content) return <>{children}</>;

  return <Tooltip content={content}>{children}</Tooltip>;
};

interface GetTooltipContentOptions {
  app: AppDetails;
  isCurrentClient: boolean;
  name: ClientAvatarProps["name"];
  variant: ClientAvatarProps["variant"];
}

const getTooltipContent = ({
  app,
  isCurrentClient,
  name,
  variant,
}: GetTooltipContentOptions): string | undefined => {
  if (isCurrentClient) {
    return "You";
  }

  if (app.id !== appId && app.id !== fallbackAppId) {
    if (variant === "row") {
      return `${name} / ${app.name}`;
    } else {
      return app.name;
    }
  }

  if (variant === "row") {
    return name;
  }

  return undefined;
};

export const ClientAvatar: React.FC<ClientAvatarProps> = ({
  appBackgroundColor,
  appId = "unknown",
  color,
  dimmed,
  isCurrentClient = false,
  name,
  onClick,
  src,
  variant,
  ...props
}) => {
  const app = appDetailsMap[appId] || appDetailsMap.unknown;
  const tooltipContent = getTooltipContent({
    app,
    isCurrentClient,
    name,
    variant,
  });

  return (
    <OptionalTooltip content={tooltipContent}>
      <Box
        css={{
          position: "relative",
          // Keeps client avatars touching each other, so there is no tooltip "flash"
          // when hovering over them (the tooltip instantlty changes)
          px: variant === "row" ? "$1" : "$0",
          transition: "opacity $fast",

          "&:hover": {
            opacity: isCurrentClient ? 1 : 0.8,
          },
        }}
        {...props}
      >
        <FocusableAvatar
          dimmed={Boolean(dimmed)}
          highlightColor={color}
          name={name}
          onClick={onClick}
          shape="circle"
          size="m"
          src={src}
        />

        {app.image && (
          <ClientSourceWrapper
            css={{
              backgroundColor: appBackgroundColor,
              color,
              // move 5px to the right of the avatar, but accounting
              // for the parent padding that's applied when rendered
              // in a `row`
              right: variant === "row" ? "-1px" : "-5px",
            }}
          >
            {app.image && <AppImage src={app.image} />}
          </ClientSourceWrapper>
        )}
      </Box>
    </OptionalTooltip>
  );
};
